import React, { useState } from 'react';
import styled from 'styled-components';
import { FiLink, FiX, FiCheckCircle, FiSettings, FiTerminal } from 'react-icons/fi';
import { colors } from '../../theme/colors';
import useWeightScale from '../../hooks/useWeightScale';

/**
 * WeightScaleWidget — POS weight-scale plugin UI.
 * Mounted by POSScreen ONLY when the `pos_weight_scale` module is enabled.
 * Self-contained: owns the Web Serial connection via useWeightScale and reports
 * a captured weight (kg) back through `onCapture`. POS stays otherwise untouched.
 *
 * Props:
 *   onCapture(weightKg)  — called when the cashier taps "Capture Weight"
 *   captureLabel         — name of the active weighable line (null disables capture)
 */
const BAUD_OPTIONS = [9600, 4800, 19200, 38400, 115200];

const WeightScaleWidget = ({ onCapture, captureLabel }) => {
  const scale = useWeightScale(true);
  const [showSettings, setShowSettings] = useState(false);
  const [showDebug, setShowDebug] = useState(false);
  const [flash, setFlash] = useState(false);

  const handleCapture = () => {
    if (!captureLabel || !onCapture) return;
    onCapture(scale.liveWeight);
    setFlash(true);
    setTimeout(() => setFlash(false), 600);
  };

  if (!scale.supported) {
    return <Bar><Muted>⚖ Weight scale needs Chrome or Edge to connect.</Muted></Bar>;
  }

  return (
    <Bar>
      <Left>
        <ScaleIcon>⚖</ScaleIcon>
        {scale.connected ? (
          <>
            <Reading $flash={flash}>{scale.liveWeight.toFixed(3)}<small>kg</small></Reading>
            <Dot $stable={scale.stable} title={scale.stable ? 'Stable' : 'Stabilising…'} />
            <StableText $stable={scale.stable}>{scale.stable ? 'stable' : '…'}</StableText>
          </>
        ) : (
          <Muted>Scale not connected</Muted>
        )}
      </Left>

      <Right>
        {scale.connected ? (
          <>
            <CaptureBtn onClick={handleCapture} disabled={!captureLabel} title={captureLabel ? `Capture into: ${captureLabel}` : 'Add/select a "sold by weight" item first'}>
              <FiCheckCircle /> Capture{captureLabel ? ` → ${captureLabel}` : ''}
            </CaptureBtn>
            <GhostBtn onClick={scale.tare} title="Zero the current reading (tare)">Tare</GhostBtn>
            <GhostBtn onClick={scale.clearTare} title="Clear tare">Clear</GhostBtn>
            <IconBtn onClick={() => setShowDebug(s => !s)} title="Raw stream (debug)"><FiTerminal /></IconBtn>
            <IconBtn onClick={scale.disconnect} title="Disconnect"><FiX /></IconBtn>
          </>
        ) : (
          <>
            <ConnectBtn onClick={scale.connect}><FiLink /> Connect Scale</ConnectBtn>
            <IconBtn onClick={() => setShowSettings(s => !s)} title="Scale settings"><FiSettings /></IconBtn>
          </>
        )}
      </Right>

      {scale.error && <ErrorLine>{scale.error}</ErrorLine>}

      {showSettings && !scale.connected && (
        <Panel>
          <label>Baud rate</label>
          <select value={scale.baudRate} onChange={e => scale.setBaudRate(Number(e.target.value))}>
            {BAUD_OPTIONS.map(b => <option key={b} value={b}>{b}</option>)}
          </select>
          <Hint>Most scales use 9600 (8-N-1). If the reading looks wrong, try another baud, then re-connect.</Hint>
        </Panel>
      )}

      {showDebug && scale.connected && (
        <Panel>
          <label>Raw stream (last 20 frames)</label>
          <Debug>{scale.rawFrames.length ? scale.rawFrames.map((f, i) => <div key={i}>{f}</div>) : <em>waiting for data…</em>}</Debug>
          <Hint>Use this to confirm the scale's format on install.</Hint>
        </Panel>
      )}
    </Bar>
  );
};

const Bar = styled.div`
  display: flex; flex-wrap: wrap; align-items: center; gap: 10px;
  padding: 8px 12px; margin-bottom: 10px;
  background: ${colors.primary.main}08; border: 1px solid ${colors.primary.main}25;
  border-radius: 10px;
`;
const Left = styled.div`display:flex; align-items:center; gap:8px; flex:1; min-width:160px;`;
const Right = styled.div`display:flex; align-items:center; gap:6px; flex-wrap:wrap;`;
const ScaleIcon = styled.span`font-size:18px;`;
const Reading = styled.span`
  font-family:'Consolas',monospace; font-size:22px; font-weight:700;
  color:${p => p.$flash ? colors.success.main : colors.text.primary}; transition:color .2s;
  small{ font-size:12px; font-weight:500; color:${colors.text.secondary}; margin-left:3px; }
`;
const Dot = styled.span`width:10px;height:10px;border-radius:50%;background:${p => p.$stable ? colors.success.main : colors.warning.main};`;
const StableText = styled.span`font-size:11px;color:${p => p.$stable ? colors.success.main : colors.warning.main};`;
const Muted = styled.span`font-size:13px;color:${colors.text.secondary};`;
const baseBtn = `display:inline-flex;align-items:center;gap:6px;padding:8px 12px;border-radius:8px;font-size:13px;font-weight:600;cursor:pointer;border:none;`;
const CaptureBtn = styled.button`${baseBtn} background:${colors.success.main};color:#fff;&:disabled{opacity:.45;cursor:not-allowed;}`;
const ConnectBtn = styled.button`${baseBtn} background:${colors.primary.main};color:#fff;`;
const GhostBtn = styled.button`${baseBtn} background:#fff;border:1px solid ${colors.neutral[300]};color:${colors.text.primary};`;
const IconBtn = styled.button`${baseBtn} background:#fff;border:1px solid ${colors.neutral[300]};color:${colors.text.secondary};padding:8px 10px;`;
const ErrorLine = styled.div`flex-basis:100%;font-size:12px;color:${colors.error.main};`;
const Panel = styled.div`flex-basis:100%;display:flex;flex-direction:column;gap:6px;padding:8px;background:#fff;border:1px solid ${colors.neutral[200]};border-radius:8px;
  label{font-size:11px;font-weight:600;color:${colors.text.secondary};text-transform:uppercase;}
  select{padding:6px 8px;border:1px solid ${colors.neutral[300]};border-radius:6px;font-size:13px;width:140px;}`;
const Hint = styled.div`font-size:11px;color:${colors.text.tertiary};`;
const Debug = styled.div`font-family:'Consolas',monospace;font-size:11px;color:${colors.text.primary};background:${colors.neutral[50]};border:1px solid ${colors.neutral[200]};border-radius:6px;padding:6px;max-height:120px;overflow:auto;white-space:pre-wrap;`;

export default WeightScaleWidget;
